<template>
  <div class="demo">
    <div id="glview"></div>

    <progress-ring
      id="progress"
      ref="prog"
      :percent="progressPct"
      :width="progressSize"
      :height="progressSize"
      :strokeWidth="3"
      :showTrack="false"
      :rgbaTrackColor="'rgba(0, 0, 0, 0.1)'"
      :rgbaRingColor="ringColor"
      :alwaysShow="true"
      :forcePow2="true"
      @onComplete="onProgressComplete" />

    <div class="intro d-flex flex-column align-items-center justify-content-center" ref="intro" v-if="introActive">
      <div class="intro-item">
        <next-mobile-decade class="nmd" />
      </div>
      <ar-icon class="icon intro-item" :complete="iconComplete" :active="iconActive" :isButton="true" />
      <h3 class="adobe-clean-serif intro-item text-center">Look around your environment<br>and color the world.</h3>
    </div>

    <div class="hint d-flex align-items-center justify-content-center" :class="{ white: targetActive }" ref="hint">
      <h5>HINT: <span>Move &amp; discover.</span></h5>
    </div>

    <div v-if="isComplete" class="selection white">
      <div class="interstitial d-flex flex-column align-items-center justify-content-center">
        <ar-icon class="icon select-item" :white="true" :complete="iconComplete" :active="true" :isButton="true" />
        <h3 class="knockout-50 select-item">COMPLETED</h3>
        <p class="text-center adobe-clean-serif text-italic select-item">
          Beautiful stuff. Your report now reflects your vision.
        </p>
      </div>

      <div v-if="outroActive"
        ref="outro"
        class="outro d-flex flex-column align-items-center user-select-none justify-content-center"
        @click="$router.push('/report')">
          <div class="outro-item">
            <next-mobile-decade class="nmd" fill="#ffffff" />
          </div>
          <p class="adobe-clean-serif text-center outro-item">
            Digital will become physical, and vice versa. Augment reality.
          </p>
          <div class="cta d-flex flex-column justify-content-end outro-item">
            <span>Tap to continue</span>
          </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import { TweenMax, TimelineMax, Elastic, Power2, Power3 } from 'TweenMax'
import THREE from '@/lib/three-extra'
import GLViewMixin from '@/mixins/GLViewMixin'
import { noiseShader, starfieldShader } from '@/lib/shaders'
import { colors } from '@/lib/consts'
import { nearestPow2 } from '@/lib/funcs'
import ProgressRing from './ProgressRing'
import Chevron from '@/components/svg/Chevron'
import ArIcon from '@/components/svg/ArIcon'
import NextMobileDecade from '@/components/svg/NextMobileDecade'

export default {
  name: 'demo-terrain',
  mixins: [GLViewMixin],
  components: {
    ProgressRing,
    Chevron,
    ArIcon,
    NextMobileDecade
  },
  data () {
    return {
      skipIntro: false, // <-- set to false for prod
      idx: 3,
      time: 0,
      fullWidth: 0,
      fullHeight: 0,
      introActive: true,
      introDelay: 3,
      interstitialActive: false,
      outroActive: false,
      isComplete: false,
      shouldUpdate: true,
      lights: [],
      plane: {
        geometry: new THREE.PlaneBufferGeometry(600, 600, 200, 200),
        material: undefined,
        color: new THREE.Color(0x333333),
        default: new THREE.Color(0x333333),
        accent: new THREE.Color(0xfafafa)
      },
      shapes: [],
      shapeDistance: 50,
      shapeRadius: 12,
      raycastTargets: [],
      raycaster: new THREE.Raycaster(),
      targetActive: false,
      activeShape: undefined,
      defaultColor: { main: new THREE.Color(0x333333), accent: new THREE.Color(0xfafafa) },
      lastActiveColor: { main: new THREE.Color(0x333333), accent: new THREE.Color(0xfafafa) },
      lastActiveIndex: 0,
      colorTimeline: undefined,
      colorProgress: 0,
      maxStars: 250,
      progressPct: 0,
      progressAddAmt: 0.0125,
      progressSubAmt: 0.025,
      timelines: [],
      tweenTargets: [],
      iconActive: true
    }
  },
  computed: {
    colors () {
      let colorList = []
      colors.forEach(color => {
        colorList.push({
          main: new THREE.Color(color.main),
          accent: new THREE.Color(color.accent)
        })
      })
      return colorList
    },
    progressSize () {
      return nearestPow2( this.fullWidth * 0.5 )
    },
    ringColor () {
      let color = this.colors[this.lastActiveIndex].accent
      return `rgba(${color.r * 255}, ${color.g * 255}, ${color.b * 255}, 0.6)`
      // return 'rgba(250, 250, 250, 0.8)'
    },
    ...mapState({
      didViewReport: state => state.report.didView
    }),
    ...mapState('demo3', {
      iconComplete: state => state.complete
    })
  },
  watch: {
    lastActiveColor ( nC ) {
      this.progressPct = 0
      this.planeTween.invalidate().updateTo({
        r: nC.accent.r,
        g: nC.accent.g,
        b: nC.accent.b
      }, false)
      this.bgTween.invalidate().updateTo({
        r: nC.main.r,
        g: nC.main.g,
        b: nC.main.b
      }, false)
      this.fogTween.invalidate().updateTo({
        r: nC.accent.r,
        g: nC.accent.g,
        b: nC.accent.b
      }, false)
    },
    progressPct () {
      this.$refs.prog.update()
      this.progressContainer.userData.canvasTexture.needsUpdate = true
    },
    targetActive () {
      if ( this.targetActive ) {
        TweenMax.to(this.shapes[this.activeShape.object.userData.index].mesh.scale, 0.5, {
          x: 1.1,
          y: 1.1,
          z: 1.1,
          Ease: Elastic.easeOut
        })
      } else {
        TweenMax.to(this.shapes[this.lastActiveIndex].mesh.scale, 0.5, {
          x: 1,
          y: 1,
          z: 1,
          Ease: Elastic.easeOut
        })
      }
    }
  },
  methods: {
    setupScene () {
      this.camera.fov = 70
      this.camera.far = 600
      this.camera.position.set(0, 10, 0)
      this.camera.lookAt(0, 0, 0)
      this.raycaster.far = this.shapeDistance

      this.controls = new THREE.DeviceOrientationControls(this.camera)

      this.scene.background = new THREE.Color(0xfafafa)
      this.scene.fog = new THREE.FogExp2(0xfafafa, 0.005)

      this.loader = new THREE.TextureLoader()

      this.plane.uniforms = {
        customColor: { value: this.plane.color },
        opacity: { value: 1.0 },
        time: { value: 1.0 },
        texture: { value: this.loader.load( require('@/assets/img/demos/particle2.png') ) },
        resolution: { value: new THREE.Vector2() },
        ...THREE.UniformsLib.fog
      }

      this.plane.material = new THREE.ShaderMaterial( {
        uniforms: this.plane.uniforms,
        vertexShader: noiseShader.vertex,
        fragmentShader: noiseShader.fragment,
        // blending: THREE.AdditiveBlending,
        depthTest: true,
        transparent: true,
        fog: true
      } )

      this.plane.mesh = new THREE.Points( this.plane.geometry, this.plane.material )
      this.plane.mesh.position.set(0, -50, 0)
      this.plane.mesh.rotation.x = THREE.Math.degToRad(-90)
      this.scene.add(this.plane.mesh)

      this.objContainer = new THREE.Object3D()
      this.colors.forEach((color, index) => {
        let shapeContainer = new THREE.Object3D()
        let geom = new THREE.IcosahedronGeometry(this.shapeRadius, 2)
        let mat = new THREE.MeshPhongMaterial({
          color: color.accent,
          emissive: color.main,
          flatShading: true,
          shininess: 50
        })
        let mesh = new THREE.Mesh(geom, mat)
        mesh.position.set(0, 5, 500)
        mesh.userData.index = index
        let spotLight = new THREE.SpotLight(0xffffff, 1, 0, 1, 1)
        spotLight.position.set(0, 10, this.shapeDistance / 2)
        spotLight.target = mesh
        shapeContainer.add(spotLight)
        shapeContainer.add(mesh)
        this.raycastTargets.push(mesh)
        shapeContainer.rotation.set(0, ((Math.PI * 2) / this.colors.length) * index, 0)
        this.objContainer.add(shapeContainer)

        // Starfields
        let pointImg = new THREE.TextureLoader().load(require('@/assets/img/demos/particle2.png'))
        let starfieldGeo = new THREE.BufferGeometry()
        let starPos = []
        let starTargets = []
        let starColors = []
        let starSizes = []
        let starAlphas = []
        for (let i = 0; i < this.maxStars; i++) {
          let cosTheta = 2 * Math.random() - 1
          let sinTheta = Math.sqrt( 1 - cosTheta * cosTheta )
          let phi = Math.random() * ( Math.PI * 2 )
          let randSX = ( this.shapeRadius * 0.5 ) * sinTheta * Math.cos( phi )
          let randSY = ( this.shapeRadius * 0.5 ) * sinTheta * Math.sin( phi )
          let randSZ = ( this.shapeRadius * 0.5 ) * cosTheta
          starPos.push( randSX )
          starPos.push( randSY )
          starPos.push( randSZ )
          let randX = ( this.shapeRadius * 5 ) * sinTheta * Math.cos( phi )
          let randY = ( this.shapeRadius * 5 ) * sinTheta * Math.sin( phi )
          let randZ = ( this.shapeRadius * 5 ) * cosTheta
          starTargets.push( THREE.Math.randFloatSpread(randX) )
          starTargets.push( THREE.Math.randFloatSpread(randY) )
          starTargets.push( THREE.Math.randFloatSpread(randZ) )
          starColors.push( color.accent.r, color.accent.g, color.accent.b )
          starAlphas.push( THREE.Math.randFloat(0.6, 0.9) )
          starSizes.push( 8 )
        }

        starfieldGeo.addAttribute( 'position', new THREE.Float32BufferAttribute( starPos, 3 ) )
        starfieldGeo.addAttribute( 'nPosition', new THREE.Float32BufferAttribute( starTargets, 3 ) )
        starfieldGeo.addAttribute( 'color', new THREE.Float32BufferAttribute( starColors, 3 ) )
        starfieldGeo.addAttribute( 'alpha', new THREE.Float32BufferAttribute( starAlphas, 1 ) )
        starfieldGeo.addAttribute( 'size', new THREE.Float32BufferAttribute( starSizes, 1 ) )

        let starUniforms = {
          opacity: { value: 0.8 },
          texture: { value: pointImg },
          progress: { value: 0.0 },
          sizeFactor: { value: 150.0 }
        }

        let starMaterial = new THREE.ShaderMaterial( {
          uniforms: starUniforms,
          vertexShader: starfieldShader.vertex,
          fragmentShader: starfieldShader.fragment,
          // blending: THREE.AdditiveBlending,
          depthTest: true,
          alphaTest: 0.4,
          transparent: true,
          vertexColors: true
        })

        let colorStarfield = new THREE.Points(starfieldGeo, starMaterial)
        colorStarfield.position.set(0, 5, this.shapeDistance)
        shapeContainer.add(colorStarfield)

        this.tweenTargets.push(mesh.scale)

        this.shapes.push({
          index: index,
          geometry: geom,
          material: mat,
          mesh: mesh,
          container: shapeContainer,
          lightSource: spotLight,
          starfield: colorStarfield,
          starUniforms: starUniforms,
          progress: 0
        })
      })

      this.progressContainer = new THREE.Object3D()

      let canvasTex = new THREE.CanvasTexture( this.$refs.prog.$el )

      let canvasMat = new THREE.MeshBasicMaterial({
        map: canvasTex,
        side: THREE.DoubleSide,
        transparent: true,
        blending: THREE.AdditiveBlending,
        alphaTest: 0.25
      })

      let canvasGeom = new THREE.PlaneGeometry( 32, 32 )

      let canvasMesh = new THREE.Mesh( canvasGeom, canvasMat )
      canvasMesh.position.set(0, 5, this.shapeDistance - this.shapeRadius / 3)
      canvasMesh.rotation.y = Math.PI
      this.progressContainer.userData.canvasTexture = canvasTex
      this.progressContainer.userData.mesh = canvasMesh
      this.progressContainer.add(canvasMesh)
      this.objContainer.add(this.progressContainer)
      this.objContainer.rotation.y = ((Math.PI * 2) / this.colors.length) / 2
      this.scene.add(this.objContainer)

      this.planeTween = TweenMax.fromTo(this.plane.uniforms.customColor.value, 2,
        {
          paused: true,
          r: this.defaultColor.main.r,
          g: this.defaultColor.main.g,
          b: this.defaultColor.main.b
        },
        {
          r: this.lastActiveColor.accent.r,
          g: this.lastActiveColor.accent.g,
          b: this.lastActiveColor.accent.b,
          ease: Power3.easeOut
        }
      )

      this.bgTween = TweenMax.fromTo(this.scene.background, 2,
        {
          paused: true,
          r: this.defaultColor.accent.r,
          g: this.defaultColor.accent.g,
          b: this.defaultColor.accent.b
        },
        {
          r: this.lastActiveColor.main.r,
          g: this.lastActiveColor.main.g,
          b: this.lastActiveColor.main.b,
          ease: Power3.easeOut
        }
      )
      this.fogTween = TweenMax.fromTo(this.scene.fog.color, 2,
        {
          paused: true,
          r: this.defaultColor.accent.r,
          g: this.defaultColor.accent.g,
          b: this.defaultColor.accent.b
        },
        {
          r: this.lastActiveColor.main.r,
          g: this.lastActiveColor.main.g,
          b: this.lastActiveColor.main.b,
          ease: Power3.easeOut
        }
      )
      this.colorTimeline = new TimelineMax({ paused: true })
      this.colorTimeline.add(this.planeTween)
      this.colorTimeline.add(this.bgTween, 0)
      this.colorTimeline.add(this.fogTween, 0)
      this.onResize()
    },
    update ( dt ) {
      if ( this.shouldUpdate ) {
        this.controls.update()
        this.time += dt / 2

        if ( this.shapes.length > 0 ) {
          this.shapes.forEach((shape, index) => {
            if ( this.targetActive && index === this.lastActiveIndex ) {
              shape.starfield.rotation.x -= 0.005
              shape.starfield.rotation.y -= 0.0075
              shape.mesh.rotation.x += 0.01
              shape.mesh.rotation.y += 0.015
            } else {
              if ( shape.progress > 0 ) {
                shape.progress -= 0.025
                shape.starUniforms.progress.value = THREE.Math.clamp(shape.progress, 0, 1)
              }
              shape.mesh.rotation.x += 0.0025
              shape.mesh.rotation.y += 0.00375
            }
          })
        }

        this.raycaster.set( this.camera.position, this.camera.getWorldDirection() )
        if ( this.raycastTargets.length > 0 && !this.isComplete) {
          let intersects = this.raycaster.intersectObjects( this.raycastTargets )
          if ( intersects.length > 0 ) {
            this.activeShape = intersects[0]
            this.targetActive = true
            if ( this.lastActiveIndex !== this.activeShape.object.userData.index ) {
              this.progressPct = 0
            }
            this.lastActiveIndex = this.activeShape.object.userData.index
            this.lastActiveColor = this.colors[this.lastActiveIndex]
            this.colorProgress += 0.005

            this.shapes[this.lastActiveIndex].progress += 0.02
            this.shapes[this.lastActiveIndex].progress = THREE.Math.clamp(this.shapes[this.lastActiveIndex].progress, 0, 1)
            // this.shapes[this.lastActiveIndex].starUniforms.progress.value = this.shapes[this.lastActiveIndex].progress

            if ( this.shapes[this.lastActiveIndex].progress > 0.5 ) {
              this.progressPct += this.progressAddAmt
            } else {
              this.progressPct -= this.progressSubAmt
            }
          } else {
            this.activeShape = undefined
            this.targetActive = false
            this.shapes[this.lastActiveIndex].progress -= 0.025
            this.shapes[this.lastActiveIndex].progress = THREE.Math.clamp(this.shapes[this.lastActiveIndex].progress, 0, 1)
            // if ( this.shapes[this.lastActiveIndex].progress > 0 )
            this.colorProgress -= 0.025
            this.progressPct -= this.progressSubAmt
          }
        }

        this.progressPct = THREE.Math.clamp(this.progressPct, 0, 1)
        if ( this.progressPct > 0 ) {
          if ( this.targetActive ) {
            if (this.progressContainer.rotation.y !== this.activeShape.object.parent.rotation.y) {
              this.progressContainer.rotation.y = this.activeShape.object.parent.rotation.y
            }
          }
        }

        this.shapes[ this.lastActiveIndex ].starUniforms.progress.value = this.shapes[ this.lastActiveIndex ].progress
        this.colorProgress = THREE.Math.clamp( this.colorProgress, 0, 1 )
        this.planeTween.progress( this.colorProgress )
        this.bgTween.progress( this.colorProgress )
        this.fogTween.progress( this.colorProgress )

        this.plane.material.uniforms.time.value = this.time
        this.plane.uniforms.needsUpdate = true

        // this.plane.material.needsUpdate = true
        this.camera.updateProjectionMatrix()
      }
    },
    onProgressComplete () {
      this.controls.enabled = false
      this.isComplete = true
      this.$store.dispatch( 'setColors', this.lastActiveIndex )
      this.$store.dispatch( 'setDemoComplete', this.idx )
      TweenMax.to( this.$refs.hint, 0.75, {
        opacity: 0,
        y: -100,
        ease: Power2.easeIn
      } )
      this.showInterstitial()
    },
    showIntro () {
      this.$store.dispatch( 'enableDemo', this.idx )
      this.$store.dispatch( 'activateDemo', this.idx )
      TweenMax.staggerTo( '.intro-item', 1, {
        delay: 0.5,
        opacity: 1,
        y: 0,
        rotationX: 0,
        rotationY: 0,
        ease: Power2.easeOut
      }, 0.15 )
      TweenMax.delayedCall( this.introDelay, this.initScene )
      this.tweenTargets.push( '.intro-item' )
      this.tweenTargets.push( this.initScene )
    },
    initScene () {
      this.$parent.showControls()
      this.animate()
      this.iconActive = false

      if (this.skipIntro) {
        this.introActive = false
        this.controls.enabled = true
      } else {
        TweenMax.to( this.$refs.intro, 0.6, {
          opacity: 0,
          ease: Power2.easeInOut,
          onComplete: () => {
            this.introActive = false
            this.controls.enabled = true
          }
        } )
      }
      this.shapes.forEach( ( shape, index ) => {
        TweenMax.to( shape.material, 0.5, {
          delay: index * 0.05,
          opacity: 1,
          ease: Elastic.easeOut.config( 0.5 ),
          onUpdate: () => {
            shape.material.needsUpdate = true
          }
        } )
        TweenMax.to( shape.mesh.position, 0.5, {
          delay: index * 0.05,
          z: this.shapeDistance,
          ease: Power3.easeOut
        } )
        this.tweenTargets.push( shape.material )
        this.tweenTargets.push( shape.mesh.position )
      })
      this.tweenTargets.push( this.$refs.intro )
      this.showHint()
    },
    showHint () {
      TweenMax.to( this.$refs.hint, 1, {
        delay: 2,
        opacity: 1,
        y: 0,
        ease: Elastic.easeOut.config( 0.25 )
      } )
      this.tweenTargets.push( this.$refs.hint )
    },
    showInterstitial () {
      this.$nextTick(() => {
        TweenMax.set( '.selection', { y: 50, opacity: 0 } )
        TweenMax.set( '.select-item', { y: 20, opacity: 0, rotationX: 20, rotationY: 50 } )
        let interTl = new TimelineMax()

        interTl.add( TweenMax.to( this.shapes[this.lastActiveIndex].mesh.scale, 1, {
          x: 0.001,
          y: 0.001,
          z: 0.001,
          ease: Power3.easeInOut
        } ) )

        interTl.add( TweenMax.to( this.progressContainer.userData.mesh.material, 1, {
          opacity: 0,
          ease: Power3.easeOut,
          onUpdate: () => {
            this.progressContainer.userData.mesh.material.needsUpdate = true
          }
        } ), 0 )

        interTl.add( TweenMax.to( this.shapes[this.lastActiveIndex].starUniforms.opacity, 1, {
          value: 0.5,
          ease: Power3.easeOut
        } ), 0 )

        interTl.add( TweenMax.to( this.plane.uniforms.opacity, 1, {
          value: 0.5,
          ease: Power3.easeOut
        } ), 0 )

        interTl.add( TweenMax.to( this.scene.fog.color, 1, {
          r: this.lastActiveColor.main.r,
          g: this.lastActiveColor.main.g,
          b: this.lastActiveColor.main.b
        } ) )

        interTl.add( TweenMax.to( '.selection', 1, {
          y: 0,
          opacity: 1,
          ease: Power2.easeOut
        } ) )

        interTl.add( TweenMax.staggerTo( '.select-item', 1, {
          y: 0,
          opacity: 1,
          rotationX: 0,
          rotationY: 0,
          ease: Power2.easeOut
        }, 0.15 ), '-=0.5' )
        interTl.add( TweenMax.delayedCall( 3, this.showOutro ) )

        this.timelines.push( interTl )
      })
    },
    showOutro () {
      this.outroActive = true
      this.$nextTick(() => {
        TweenMax.set( '.outro', { opacity: 0 } )
        TweenMax.set( '.outro-item', { opacity: 0, y: 25, rotationX: 20, rotationY: 50 } )
        let outroTl = new TimelineMax()
        outroTl.add( TweenMax.staggerTo( '.select-item', 0.75, {
          y: -20,
          opacity: 0,
          ease: Power2.easeIn
        }, 0.1 ) )
        outroTl.add( TweenMax.to( '.outro', 0.75, {
          opacity: 1,
          ease: Power2.easeOut
        } ) )
        outroTl.add( TweenMax.staggerTo( '.outro-item', 1, {
          y: 0,
          opacity: 1,
          rotationX: 0,
          rotationY: 0,
          ease: Power2.easeOut
        }, 0.15 ) )
        this.timelines.push( outroTl )
      })
    },
    killAllTimelines () {
      this.timelines.forEach( tl => tl.kill() )
    }
  },
  created () {
    window.addEventListener('resize', this.onResize)
  },
  mounted () {
    this.fullWidth = (window.screen) ? screen.availWidth : window.innerWidth
    this.fullHeight = (window.screen) ? screen.availHeight : window.innerHeight
    this.$nextTick(() => {
      this.init('glview')
      this.setupScene()
      TweenMax.set( this.$refs.hint, { opacity: 0, y: -100 } )
      TweenMax.set( '.intro-item', { opacity: 0, y: 20, rotationX: 20, rotationY: 50 } )
      TweenMax.set( '.icon.intro-item', { opacity: 0, y: 20 } )
      if (!this.skipIntro) {
        this.showIntro()
      } else {
        this.initScene()
      }
    })
  },
  beforeDestroy () {
    this.$store.dispatch( 'deactivateDemo', this.idx )
    TweenMax.killTweensOf( this.tweenTargets )
    this.killAllTimelines()
    this.controls.enabled = false
    window.removeEventListener('resize', this.onResize)
    cancelAnimationFrame(this.rafID)
  }
}
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/global/components/demo-common";

.demo {

  #progress {
    display: none;
  }

}
</style>



// WEBPACK FOOTER //
// src/components/demos/DemoTerrain.vue